import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  type OnChanges,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { Subscription } from 'rxjs';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import {
  type AbstractControl,
  type FormArray,
  FormBuilder,
  type FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { CurrencySelectorComponent } from '../currency-selector/currency-selector.component';
import { EnumOptionsEditorComponent } from '../enum-options-editor/enum-options-editor.component';
import { DropdownComponent } from '@bizzdesign/ui-dropdown';
import { HelperTextComponent } from '@bizzdesign/ui-helper-text';
import { createBDCoreObjectTypeOptions } from '@bizzdesign/shared/utils';
import { AttributeDefinitionForm } from '../../models/attribute-definition-form.model';
import { getAttributeTypeOptions } from '../../utils/attribute-type.utils';
import { generateUUID, type UUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import { type AttributeDefinition } from '@bizzdesign/shared/models';
import { SelectModule } from 'primeng/select';
import { type CurrencyCodeType } from '@bizzdesign/sdk-knowledgegraph';

@Component({
  selector: 'bd-attributes-editor',
  imports: [
    ButtonModule,
    ReactiveFormsModule,
    InputTextModule,
    SelectModule,
    SharedI18nModule,
    CurrencySelectorComponent,
    EnumOptionsEditorComponent,
    DropdownComponent,
    HelperTextComponent,
  ],
  templateUrl: './attributes-editor.component.html',
  styleUrl: './attributes-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttributesEditorComponent implements OnChanges {
  private fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private valueChangesSubscription?: Subscription;

  readonly objectTypeOptions = createBDCoreObjectTypeOptions();

  constructor() {
    this.createEmptyForm();
  }

  private createEmptyForm() {
    this.editorForm = this.fb.group({
      attributes: this.fb.array<FormGroup<AttributeDefinitionForm>>([]),
    });
  }

  readonly attributeTypeOptions = getAttributeTypeOptions();

  editorForm!: FormGroup<{
    attributes: FormArray<FormGroup<AttributeDefinitionForm>>;
  }>;

  @Input() existingAttributes: string[] | null = null;

  @Output() newAttributes = new EventEmitter<AttributeDefinition[]>();

  ngOnChanges(): void {
    this.valueChangesSubscription?.unsubscribe();
    this.newAttributes.emit([]);
    if (this.existingAttributes === null) {
      this.createEmptyForm();
      return;
    }
    this.editorForm = this.fb.group<{
      attributes: FormArray<FormGroup<AttributeDefinitionForm>>;
    }>({
      attributes: this.fb.array<FormGroup<AttributeDefinitionForm>>(
        [],
        (control) => {
          const attributes = control.value as AttributeDefinition[];
          const newNamesUsageCount = attributes.reduce(
            (acc: { [key: string]: number }, attribute) => {
              if (!attribute.name) return acc;
              acc[attribute.name.toLowerCase()] =
                (acc[attribute.name.toLowerCase()] ?? 0) + 1;
              return acc;
            },
            {},
          );
          for (let attributeControl of (
            control as FormArray<FormGroup<AttributeDefinitionForm>>
          ).controls) {
            const nameControl = attributeControl.get('name')!;
            if (
              nameControl.value &&
              (this.existingAttributes?.some(
                (attribute) =>
                  attribute.toLowerCase() === nameControl.value!.toLowerCase(),
              ) ||
                newNamesUsageCount[nameControl.value!.toLowerCase()] > 1)
            ) {
              nameControl.setErrors({
                ...nameControl.errors,
                unique: true,
              });
            } else if (nameControl.hasError('unique')) {
              const errors = { ...nameControl.errors };
              delete errors['unique'];
              nameControl.setErrors(
                Object.keys(errors).length > 0 ? errors : null,
              );
            }
          }
          return null;
        },
      ),
    });
    this.valueChangesSubscription = this.editorForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const newAttributes: AttributeDefinition[] = this.editorForm.valid
          ? value.attributes!.map((attribute) => {
              const baseAttrs = {
                name: attribute.name!,
                id: attribute.id as UUID,
              };

              switch (attribute.type) {
                case 'money':
                  return {
                    ...baseAttrs,
                    type: 'money',
                    currency: attribute.currency!,
                  };
                case 'reference':
                  return {
                    ...baseAttrs,
                    type: 'reference',
                    referenceType: attribute.referenceType?.data!,
                  };
                case 'enum':
                  return {
                    ...baseAttrs,
                    type: 'enum',
                    enumValues:
                      attribute.enumValues?.map((enumValue) => {
                        return {
                          id: generateUUID(),
                          name: enumValue,
                        };
                      }) ?? [],
                  };
                default:
                  return {
                    ...baseAttrs,
                    type: attribute.type!,
                  };
              }
            })
          : [];
        this.newAttributes.emit(newAttributes);
      });
    this.onAddAttributeButtonClick();
  }

  get attributesFormArray() {
    return this.editorForm.get('attributes') as FormArray;
  }

  get attributeGroupsArray() {
    return this.attributesFormArray
      .controls as FormGroup<AttributeDefinitionForm>[];
  }

  onAddAttributeButtonClick() {
    this.attributesFormArray.push(
      this.fb.group(new AttributeDefinitionForm(this.fb)),
    );
  }

  onRemoveAttributeButtonClick(index: number) {
    if (this.attributesFormArray.length === 1) {
      this.attributesFormArray.reset();
    } else {
      this.attributesFormArray.removeAt(index);
    }
  }

  onSelectedCurrencyChange(
    currency: CurrencyCodeType,
    attribute: FormGroup<AttributeDefinitionForm>,
  ): void {
    attribute.get('currency')!.setValue(currency);
  }

  getAttributeForm(index: number): FormGroup<AttributeDefinitionForm> {
    return this.attributesFormArray.at(
      index,
    ) as FormGroup<AttributeDefinitionForm>;
  }

  firstError(index: number, controlKey: string) {
    const typeControl = this.getAttributeForm(index).get(controlKey);
    return Object.keys(typeControl?.errors ?? { '': true })[0];
  }

  formatNameValue(control: AbstractControl) {
    if (control.value) {
      const formattedValue = control.value.trim().replace(/\s+/g, ' ');
      if (formattedValue !== control.value) {
        control.setValue(formattedValue);
      }
    }
  }
}
